import { useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { openFileDialog } from '@iconik-io/core/src/web/open_file_dialog';
import type { AssetsPublicationDraftSchemaNullable } from '@iconik-io/iconik-api/src/services/assets';
import {
  type FilesFormatSchemaWithIdNullable,
  type FilesKeyframeSchemaWithIdNullable,
  httpGetAssetsByAssetIdKeyframesByKeyframeId,
} from '@iconik-io/iconik-api/src/services/files';
import { useEntity } from '@iconik-io/shared-lib/src/react/hooks/entity/use_entity';
import { useEntitySimple } from '@iconik-io/shared-lib/src/react/hooks/entity/use_entity_simple';
import type { AppDispatch } from '@iconik-io/shared-lib/src/redux/types';
import { findBestKeyframe } from '@iconik-io/shared-lib/src/utils/find_best_keyframe';
import {
  Button,
  Icon,
  Menu,
  type MenuItemWithId,
  MenuTrigger,
  ProgressCircle,
  separateMenuGroups,
  Text,
} from '@iconik-io/uad';

import {
  createPosterFromVideoElement,
  updateKeyframeAndPosterFromFile,
} from '../../../ducks/entity_keyframes/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import type { KeyframeWithId } from '../../../types/files';
import { selectMediaFormats } from '../../asset_files/selectors';
import { SelectThumbnailAssetModal } from './select_thumbnail_asset_modal/select_thumbnail_asset_modal';

import styles from './thumbnails_selector.module.css';

export type ThumbnailSelection =
  | {
      type: 'KEYFRAME';
      keyframe: KeyframeWithId;
      previewUrl: string | undefined;
    }
  | {
      type: 'KEYFRAME_EXTERNAL_ASSET';
      keyframe: FilesKeyframeSchemaWithIdNullable;
      previewUrl: string | undefined;
      assetId: string;
    }
  | {
      type: 'FORMAT';
      format: FilesFormatSchemaWithIdNullable;
      previewUrl: string | undefined;
    };

export type ThumbnailMenuOption = 'browse' | 'snapshot' | 'upload' | undefined;

type ThumbnailsSelectorProps = {
  assetId: string;
  versionId: string | undefined;
  thumbnail: ThumbnailSelection | undefined;
  /** Thumbnail saved in the asset's publication draft, if it has one. */
  draftThumbnail: AssetsPublicationDraftSchemaNullable['thumbnail'];
  /** While true the draft is still being fetched, so no initial thumbnail is picked yet. */
  isDraftLoading: boolean;
  onChangeThumbnail: (
    thumbnailSelection: ThumbnailSelection | undefined,
  ) => void;
  onChangeSelectedOption: (option: ThumbnailMenuOption) => void;
};

export function ThumbnailsSelector({
  assetId,
  versionId,
  thumbnail,
  draftThumbnail,
  isDraftLoading,
  onChangeThumbnail,
  onChangeSelectedOption,
}: ThumbnailsSelectorProps) {
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  const { entity: asset } = useEntitySimple('assets', assetId);

  const initialKeyframeAssetId = draftThumbnail?.asset_id ?? assetId;

  const getItemKeyframe = useCallback(
    (appDispatch: AppDispatch, primaryId: string) =>
      appDispatch(
        httpGetAssetsByAssetIdKeyframesByKeyframeId(
          initialKeyframeAssetId,
          primaryId,
        ),
      ),
    [initialKeyframeAssetId],
  );

  const initialKeyframeId =
    draftThumbnail?.keyframe_id ?? asset?.custom_poster ?? undefined;

  const { entity: initialKeyframe, status: initialKeyframeStatus } = useEntity({
    entityType: 'keyframes',
    primaryId: isDraftLoading ? undefined : initialKeyframeId,
    getItem: getItemKeyframe,
  });

  const isInitialKeyframeSetRef = useRef(false);
  useEffect(() => {
    if (
      initialKeyframe &&
      initialKeyframeStatus === 'success' &&
      !isInitialKeyframeSetRef.current
    ) {
      onChangeThumbnail({
        type: 'KEYFRAME',
        keyframe: initialKeyframe,
        previewUrl: initialKeyframe.url,
      });
      isInitialKeyframeSetRef.current = true;
    }
  }, [initialKeyframeStatus, initialKeyframe, onChangeThumbnail]);

  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const iconOrLoader = isLoading ? (
    <ProgressCircle className={styles.progress} />
  ) : (
    <Icon name="poster" className={styles.icon} />
  );

  const mediaFormats = useAppSelector((state) =>
    selectMediaFormats(state, assetId, versionId),
  );
  const hasVideoTrack = mediaFormats.some((format) =>
    format.components?.some((component) => component.type === 'VIDEO'),
  );

  const menuItems: MenuItemWithId[] = separateMenuGroups([
    [
      {
        id: 'browse',
        label: 'Browse',
        icon: 'asset_type_image',
        onAction: () => {
          setIsAddAssetModalOpen(true);
        },
      },
      {
        id: 'snapshot',
        label: 'Snapshot',
        icon: 'snapshot',
        isDisabled: !hasVideoTrack,
        onAction: async () => {
          onChangeThumbnail(undefined);
          setIsLoading(true);
          const posterKeyframe = await dispatch(
            createPosterFromVideoElement({
              videoElement: document.querySelector('video'),
              assetId,
              skipKeyframeUpdate: true,
              skipCustomPosterUpdate: true,
            }),
          ).finally(() => {
            setIsLoading(false);
          });
          if (!posterKeyframe) {
            return;
          }
          onChangeThumbnail({
            type: 'KEYFRAME',
            keyframe: posterKeyframe,
            previewUrl: posterKeyframe.url,
          });
          onChangeSelectedOption('snapshot');
        },
      },
      {
        id: 'upload',
        label: 'Upload',
        icon: 'load_up',
        onAction: async () => {
          const thumbnailFiles = await openFileDialog({
            accept: 'image/*',
          });
          const firstFile = thumbnailFiles?.at(0);
          if (!firstFile) {
            return;
          }
          onChangeThumbnail(undefined);
          setIsLoading(true);
          const keyframes = await dispatch(
            updateKeyframeAndPosterFromFile({
              entity: 'assets',
              entityId: assetId,
              file: firstFile,
              skip: {
                keyframe: true,
                customPosterUpdate: true,
              },
            }),
          ).finally(() => {
            setIsLoading(false);
          });
          const firstKeyframe = keyframes?.at(0);
          if (!firstKeyframe) {
            return;
          }
          onChangeThumbnail({
            type: 'KEYFRAME',
            keyframe: firstKeyframe,
            previewUrl: firstKeyframe.url,
          });
          onChangeSelectedOption('upload');
        },
      },
    ],
    [
      {
        id: 'delete',
        label: 'Delete',
        icon: 'deletion_delete',
        onAction: () => {
          onChangeSelectedOption(undefined);
          onChangeThumbnail(undefined);
        },
        isDisabled: thumbnail === undefined,
      },
    ],
  ]);

  return (
    <div className={styles.thumbnails_selector_layout}>
      <div className={styles.thumbnails_toolbar}>
        <Text variant="value">Thumbnail</Text>
        <MenuTrigger>
          <Button
            icon={<Icon name="menu_context" size="s" />}
            variant="tertiary"
            size="s"
          />
          <Menu
            onOpenChange={setIsMenuOpen}
            isOpen={isMenuOpen}
            selectionMode="none"
            headerTitle="Thumbnail"
            items={menuItems}
          />
        </MenuTrigger>
      </div>
      <div className={classNames(styles.thumbnails_selector_wrapper)}>
        <div className={classNames(styles.thumbnails_selector)}>
          <SelectThumbnailAssetModal
            currentAssetId={assetId}
            isOpen={isAddAssetModalOpen}
            onCancel={() => setIsAddAssetModalOpen(false)}
            onConfirm={(searchItem) => {
              const bestKeyframe = findBestKeyframe(
                searchItem.keyframes ?? undefined,
              );
              if (!bestKeyframe) {
                return;
              }
              onChangeThumbnail({
                type: 'KEYFRAME_EXTERNAL_ASSET',
                keyframe: bestKeyframe,
                previewUrl: bestKeyframe.url ?? undefined,
                assetId: searchItem.id,
              });
              onChangeSelectedOption('browse');
              setIsAddAssetModalOpen(false);
            }}
          />
          {thumbnail && thumbnail.previewUrl ? (
            <img
              className={styles.selected_thumbnail}
              src={thumbnail.previewUrl}
              alt=""
            />
          ) : (
            iconOrLoader
          )}
        </div>
      </div>
    </div>
  );
}
